/**
 * InteractionLayer captures mouse/touch events and updates ChartStore.
 * Does NOT re-render on hover changes - writes only.
 *
 * Uses hooks for event handling logic:
 * - useChartInteraction: mouse events (hover, pan, click)
 * - useWheelInteraction: wheel events (zoom, horizontal scroll)
 */

import React, { useRef } from "react";

import styles from "../styles/styles.module.css";
import { useChartContext } from "../core/ChartContext";
import {
  useChartInteraction,
  useTouchInteraction,
  useWheelInteraction,
} from "~hooks/ticker-chart";
import type { ClickEvent, HoverEvent } from "~types/types";

export interface InteractionLayerProps {
  /** Series IDs to enable hit testing for */
  seriesIds: string[];
  /** Enable hover interactions */
  hoverEnabled?: boolean;
  /** Enable pan interactions */
  panEnabled?: boolean;
  /** Enable zoom interactions */
  zoomEnabled?: boolean;
  /** Hover event callback */
  onHover?: (event: HoverEvent) => void;
  /** Click event callback */
  onClick?: (event: ClickEvent) => void;
}

/**
 * InteractionLayer handles all user input.
 * Updates store state imperatively without triggering re-renders.
 */
const InteractionLayerInner: React.FC<InteractionLayerProps> = ({
  seriesIds,
  hoverEnabled = true,
  panEnabled = true,
  zoomEnabled = true,
  onHover,
  onClick,
}) => {
  const store = useChartContext();
  const svgRef = useRef<SVGRectElement>(null);

  // Mouse event handlers (hover, pan, click)
  const {
    handleMouseMove,
    handleMouseLeave,
    handleMouseDown,
    handleMouseUp,
    handleClick,
  } = useChartInteraction({
    store,
    seriesIds,
    hoverEnabled,
    panEnabled,
    onHover,
    onClick,
  });

  // Wheel event handlers (zoom, horizontal scroll)
  useWheelInteraction({
    elementRef: svgRef,
    store,
    zoomEnabled,
    panEnabled,
  });

  // Touch event handlers (pinch zoom, pan with momentum)
  useTouchInteraction({
    elementRef: svgRef,
    store,
    zoomEnabled,
    panEnabled,
    momentumEnabled: true,
  });

  const layout = store.getLayout();

  return (
    <rect
      ref={svgRef}
      className={styles.interactionLayer}
      x={layout.priceArea.x}
      y={layout.priceArea.y}
      width={layout.priceArea.width}
      height={layout.priceArea.height}
      fill="transparent"
      style={{ cursor: panEnabled ? "crosshair" : "default" }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onClick={handleClick}
      data-component="price-movement-chart-interaction-layer"
    />
  );
};

export const InteractionLayer = React.memo(InteractionLayerInner);
InteractionLayer.displayName = "InteractionLayer";
